import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import "./MaterialCategoryManager.scss";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

const MaterialCategoryManager = () => {
  const [materialCategoryAdmin, setMaterialCategoryAdmin] = useState([]);
  const [materialCategory, setMaterialCategory] = useState({
    name: { az: "", en: "", ru: "" },
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const authToken = localStorage.getItem("authToken");

  const fetchMaterialCategory = useCallback(() => {
    if (!authToken) return;
    
    setLoading(true);
    const api = axiosWithAuth();
    api.get("/material-categories/admin")
      .then((response) => {
        // Sadece aktif olanları göster
        const activeCategories = response.data.filter(category => category.status);
        setMaterialCategoryAdmin(activeCategories);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch material categories. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      Swal.fire({
        title: "Error",
        text: "Please login to access this page",
        icon: "error",
        timer: 1500,
      });
      return;
    }

    // Kullanıcı durumunu kontrol et
    const api = axiosWithAuth();
    api.get("/users/profile")
      .then((response) => {
        if (response.data.status !== 'active') {
          Swal.fire({
            title: "Error",
            text: "Your account is inactive. Please contact support.",
            icon: "error",
            timer: 3000,
          });
          return;
        }
        fetchMaterialCategory();
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Error",
          text: "Failed to verify user status. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken, fetchMaterialCategory]);

  const handleInput = (event) => {
    const { name, value, dataset } = event.target;
    const field = dataset.field;

    if (["az", "en", "ru"].includes(name)) {
      setMaterialCategory((prevType) => ({
        ...prevType,
        [field]: { ...prevType[field], [name]: value },
      }));
    } else {
      setMaterialCategory((prevType) => ({ ...prevType, [name]: value }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!authToken) return;

    setLoading(true);
    const api = axiosWithAuth();
    const method = isEditing ? 'put' : 'post';
    const url = isEditing
      ? `/material-categories/${editId}`
      : "/material-categories";

    api[method](url, materialCategory)
      .then(() => {
        setIsEditing(false);
        setEditId(null);
        setMaterialCategory({
          name: { az: "", en: "", ru: "" },
          status: true,
        });
        fetchMaterialCategory();
        setLoading(false);
        Swal.fire({
          title: "Success",
          text: isEditing ? "Material category has been updated" : "Material category has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: isEditing ? "Failed to update material category" : "Failed to add material category",
          icon: "error",
          timer: 1500,
        });
      });
  };

  const handleEdit = (type) => {
    setMaterialCategory(type);
    setEditId(type.id);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (!authToken) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#cda742",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const api = axiosWithAuth();
        api.delete(`/material-categories/${id}`)
          .then(() => {
            // Silinen kaydı listeden kaldır
            setMaterialCategoryAdmin(prevCategories => prevCategories.filter(category => category.id !== id));
            setLoading(false);
            Swal.fire({
              title: "Deleted!",
              text: "Material category has been deleted.",
              icon: "success",
              timer: 1500,
            });
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
            Swal.fire({
              title: "Error",
              text: "Failed to delete material category",
              icon: "error",
              timer: 1500,
            });
          });
      }
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditId(null);
    setMaterialCategory({
      name: { az: "", en: "", ru: "" },
      status: true,
    });
  };

  return (
    <div className="material-category-manager">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Material Categories</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
            <div className="material-category-name">
              <h5 className="section-title">Material Category Name</h5>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label" htmlFor="az">
                      Azerbaijani
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      value={materialCategory.name.az}
                      onChange={handleInput}
                      name="az"
                      data-field="name"
                      required
                      placeholder="Enter Azerbaijani name"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label" htmlFor="en">
                      English
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      value={materialCategory.name.en}
                      onChange={handleInput}
                      name="en"
                      data-field="name"
                      required
                      placeholder="Enter English name"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label" htmlFor="ru">
                      Russian
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      value={materialCategory.name.ru}
                      onChange={handleInput}
                      name="ru"
                      data-field="name"
                      required
                      placeholder="Enter Russian name"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary submit-btn">
                {isEditing ? "Update Material Category" : "Add Material Category"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  className="btn btn-secondary cancel-btn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Material Categories List</h3>
                </div>
                <div className="card-body">
                  {loading && !materialCategoryAdmin.length ? (
                    <div className="text-center my-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading material categories...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Name (AZ)</th>
                            <th>Name (EN)</th>
                            <th>Name (RU)</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {materialCategoryAdmin.length > 0 ? (
                            materialCategoryAdmin.map((type) => (
                              <tr key={type.id}>
                                <td>{type.name.az}</td>
                                <td>{type.name.en}</td>
                                <td>{type.name.ru}</td>
                                <td>
                                  <span className={`status-badge ${type.status ? "active" : "inactive"}`}>
                                    {type.status ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      className="btn btn-sm btn-primary edit-btn"
                                      onClick={() => handleEdit(type)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger delete-btn"
                                      onClick={() => handleDelete(type.id)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No material categories found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialCategoryManager;
